/* eslint-disable react/destructuring-assignment */

import type { ReactNode } from 'react';

import { Card } from '@packages/shared/src/ui/Card/Card';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { PromotionComponentLayout3Messages } from './messages';
import { TextWithDialog } from '../../../TextWithDialog';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking';
import {
  ContentWrapper,
  ContentWrapperCallToAction,
  ContentWrapperContent,
} from '../ContentWrapper';

export type Layout3Props = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component Layout 3
 *
 * component to promote the installment calculator in layout version 3
 *
 * */
export const Layout3 = ({
  singleInstallment,
  currency,
  openInstallmentCalulatorHandler,
  className,
  ...props
}: Layout3Props) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');
  const {
    installmentCalculator: { hasSpecialCreditColors },
  } = useConfig();

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  const handleOpenCalculator = () => {
    openInstallmentCalulatorHandler();
    trackGlycerinLinkClick();
  };

  return (
    /* bgColor is a special one for credit */
    /* TODO - UX has to decide if they implement this special color into design system -> https://empiriecom.atlassian.net/browse/INFORM-2597 */
    <Card
      className={cn(
        `${hasSpecialCreditColors ? 'bg-color-[rgb(238, 243, 247)]' : 'bg-color-grey-light'} shadow-color-grey`,
        className,
      )}
      {...props}
    >
      <ContentWrapper>
        <ContentWrapperContent>
          <div className="bg-color-additional flex p-1">
            <div className="icon-emp-installments text-color-background-base h-4 w-4" />
          </div>
          <div className="pb-1.5">
            <p className="text-size-h5">
              {formatMessage(PromotionComponentLayout3Messages.rateText, {
                value: formatPrice(singleInstallment, currency),
              })}
            </p>
            {/* TODO: UI Button has no focus effects */}
            <Button
              variant="default"
              size="small"
              tabIndex={0}
              className="border-color-text-darktransparent focus:border-color-text-dark mt-1 focus:shadow-sm"
              onClick={handleOpenCalculator}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleOpenCalculator();
                }
              }}
            >
              {formatMessage(PromotionComponentLayout3Messages.buttonText)}
            </Button>
          </div>
        </ContentWrapperContent>
        <ContentWrapperCallToAction>
          {formatMessage(PromotionComponentLayout3Messages.legalText, {
            button: (msg: ReactNode) => (
              <TextWithDialog
                message={msg}
                dialogTitle={formatMessage(PromotionComponentLayout3Messages.modalHeadline)}
                dialogContent={<ServiceLayer serviceLayerId="installment_ratenelement" />}
                variant="h5"
                displayTrackingProps={{ category: 'dv-service-layer-layout-3' }}
              />
            ),
          })}
        </ContentWrapperCallToAction>
      </ContentWrapper>
    </Card>
  );
};
